/**
 * Image Settings Dialog Component
 *
 * Full-screen dialog for configuring image generation settings:
 * model-in-preset toggle, auto-save toggle, basic settings (size, quality, style),
 * engine parameters, prompt enhancements with history, and preset save/load.
 */

import { useState } from 'react';
import { Info, X, History, Save, BookMarked, Check, RefreshCw, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { EngineParamsForm } from './EngineParamsForm';
import { ModelDefinitionListItem } from '@/lib/api';
import {
  ImageInstanceConfig,
  ImagePreset,
  EnhancementKey,
  ENHANCEMENT_CATEGORIES,
  EMPTY_ENHANCEMENTS,
  isUnsavedPresetName,
  formatModelName,
} from '../types/image-generator.types';

export interface ImageSettingsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activeSettingsInstance: number | null;
  config: ImageInstanceConfig | null;
  schemaLoading: boolean;
  sizeOptions: { value: string; label: string }[];
  qualityOptions: { value: string; label: string }[];
  styleOptions: { value: string; label: string }[];
  enhancementHistory: Record<EnhancementKey, string[]>;
  presets: ImagePreset[];
  availableModels: ModelDefinitionListItem[];
  // Save preset dialog state
  isSavePresetDialogOpen: boolean;
  newPresetName: string;
  // Callbacks
  onOverwritePreset: (instanceId: number) => void;
  onUpdateConfig: (instanceId: number, updates: Partial<ImageInstanceConfig>) => void;
  onUpdateEnhancement: (instanceId: number, key: EnhancementKey, value: string) => void;
  onClearEnhancement: (instanceId: number, key: EnhancementKey) => void;
  onSelectFromHistory: (instanceId: number, key: EnhancementKey, value: string) => void;
  onOpenSavePresetDialog: () => void;
  onCloseSavePresetDialog: () => void;
  onNewPresetNameChange: (value: string) => void;
  onSavePreset: () => void;
  onOpenPresetsSheet: () => void;
}

export function ImageSettingsSheet({
  open,
  onOpenChange,
  activeSettingsInstance,
  config,
  schemaLoading,
  sizeOptions,
  qualityOptions,
  styleOptions,
  enhancementHistory,
  presets,
  availableModels,
  isSavePresetDialogOpen,
  newPresetName,
  onOverwritePreset,
  onUpdateConfig,
  onUpdateEnhancement,
  onClearEnhancement,
  onSelectFromHistory,
  onOpenSavePresetDialog,
  onCloseSavePresetDialog,
  onNewPresetNameChange,
  onSavePreset,
  onOpenPresetsSheet,
}: ImageSettingsSheetProps) {
  const [basicOpen, setBasicOpen] = useState(true);
  const [engineOpen, setEngineOpen] = useState(true);
  const [enhancementsOpen, setEnhancementsOpen] = useState(true);

  const enhancements = config?.enhancements || EMPTY_ENHANCEMENTS;

  const modelDisplayName = config
    ? availableModels.find((m) => m.modelName === config.modelName)?.displayName ||
      formatModelName(config.modelName)
    : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[95vw] h-[90vh] flex flex-col p-0">
        <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-border">
          <DialogTitle className="text-lg">Image Settings</DialogTitle>
        </DialogHeader>

        {activeSettingsInstance !== null && config && (
          <>
            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="max-w-2xl mx-auto flex flex-col gap-6">
                {/* Auto-Save Settings */}
                <div className="space-y-3 pb-4 border-b border-border">
                  <div className="flex items-center gap-3">
                    <button
                      id="auto-save-checkbox"
                      type="button"
                      role="checkbox"
                      aria-checked={config.autoSave}
                      onClick={() =>
                        onUpdateConfig(activeSettingsInstance, { autoSave: !config.autoSave })
                      }
                      className={`h-5 w-5 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${
                        config.autoSave
                          ? 'bg-primary border-primary'
                          : 'border-muted-foreground/50 hover:border-muted-foreground'
                      }`}
                    >
                      {config.autoSave && <Check className="h-3.5 w-3.5 text-primary-foreground" />}
                    </button>
                    <div className="flex-1">
                      <label
                        htmlFor="auto-save-checkbox"
                        className="text-sm font-medium cursor-pointer select-none"
                      >
                        Auto-Save Settings
                      </label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Automatically restore this image generator's settings when you return
                      </p>
                    </div>
                  </div>
                </div>

                {/* Model in Preset */}
                <div className="space-y-3 pb-4 border-b border-border">
                  <div className="flex items-center gap-3">
                    <button
                      id="include-model-checkbox"
                      type="button"
                      role="checkbox"
                      aria-checked={config.includeModelInPreset}
                      onClick={() =>
                        onUpdateConfig(activeSettingsInstance, {
                          includeModelInPreset: !config.includeModelInPreset,
                        })
                      }
                      className={`h-5 w-5 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${
                        config.includeModelInPreset
                          ? 'bg-primary border-primary'
                          : 'border-muted-foreground/50 hover:border-muted-foreground'
                      }`}
                    >
                      {config.includeModelInPreset && (
                        <Check className="h-3.5 w-3.5 text-primary-foreground" />
                      )}
                    </button>
                    <div className="flex-1">
                      <label
                        htmlFor="include-model-checkbox"
                        className="text-sm font-medium cursor-pointer select-none"
                      >
                        Include Model in Preset
                      </label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Save the selected model with this preset. When unchecked, the preset can be
                        applied to any model.
                      </p>
                    </div>
                  </div>
                  {/* Current model display */}
                  <div className="ml-8 px-3 py-2 bg-muted/50 rounded-md">
                    <p className="text-xs text-muted-foreground">Current model</p>
                    <p className="text-sm font-medium">{modelDisplayName}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Provider: {config.provider}
                    </p>
                  </div>
                </div>

                {/* Basic Settings */}
                <Collapsible open={basicOpen} onOpenChange={setBasicOpen}>
                  <CollapsibleTrigger className="flex items-center gap-2 w-full py-2 cursor-pointer select-none group">
                    <ChevronDown
                      className={`h-4 w-4 text-muted-foreground transition-transform ${basicOpen ? '' : '-rotate-90'}`}
                    />
                    <h3 className="text-sm font-medium">Basic Settings</h3>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="space-y-4 pt-2 pb-4">
                      {schemaLoading && (
                        <p className="text-xs text-muted-foreground">Loading settings...</p>
                      )}

                      {/* Aspect Ratio */}
                      <div className="space-y-2">
                        <label className="text-xs text-muted-foreground">Aspect Ratio</label>
                        <Select
                          value={config.size}
                          onValueChange={(value) =>
                            onUpdateConfig(activeSettingsInstance, { size: value })
                          }
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {sizeOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Quality */}
                      <div className="space-y-2">
                        <label className="text-xs text-muted-foreground">Quality</label>
                        <Select
                          value={config.quality}
                          onValueChange={(value) =>
                            onUpdateConfig(activeSettingsInstance, { quality: value })
                          }
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {qualityOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Style */}
                      <div className="space-y-2">
                        <label className="text-xs text-muted-foreground">Style</label>
                        <Select
                          value={config.style}
                          onValueChange={(value) =>
                            onUpdateConfig(activeSettingsInstance, { style: value })
                          }
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {styleOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Engine Parameters */}
                <Collapsible open={engineOpen} onOpenChange={setEngineOpen}>
                  <div className="pt-4 border-t border-border">
                    <CollapsibleTrigger className="flex items-center gap-2 w-full py-2 cursor-pointer select-none group">
                      <ChevronDown
                        className={`h-4 w-4 text-muted-foreground transition-transform ${engineOpen ? '' : '-rotate-90'}`}
                      />
                      <h3 className="text-sm font-medium">Engine Parameters</h3>
                    </CollapsibleTrigger>
                  </div>
                  <CollapsibleContent>
                    <div className="space-y-4 pt-2 pb-4">
                      <p className="text-xs text-muted-foreground">
                        Model-specific settings saved with presets. Parameters not supported by the
                        selected model will not be applied during generation.
                      </p>
                      <EngineParamsForm
                        modelName={config.modelName}
                        providerParams={config.providerParams || {}}
                        onUpdate={(params) =>
                          onUpdateConfig(activeSettingsInstance, { providerParams: params })
                        }
                        showAllParams
                      />
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Prompt Enhancements */}
                <Collapsible open={enhancementsOpen} onOpenChange={setEnhancementsOpen}>
                  <div className="pt-4 border-t border-border">
                    <CollapsibleTrigger className="flex items-center gap-2 w-full py-2 cursor-pointer select-none group">
                      <ChevronDown
                        className={`h-4 w-4 text-muted-foreground transition-transform ${enhancementsOpen ? '' : '-rotate-90'}`}
                      />
                      <h3 className="text-sm font-medium">Prompt Enhancements</h3>
                    </CollapsibleTrigger>
                  </div>
                  <CollapsibleContent>
                    <div className="space-y-4 pt-2 pb-4">
                      <p className="text-xs text-muted-foreground">
                        Add details to guide the AI's image generation. These will be appended to
                        your prompt.
                      </p>

                      {ENHANCEMENT_CATEGORIES.map((category) => (
                        <div key={category.key} className="space-y-2">
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-muted-foreground">
                              {category.label}
                            </label>
                            <div className="relative group/info">
                              <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                              <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-popover border border-border rounded-lg shadow-lg text-xs text-popover-foreground w-64 opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all z-[100]">
                                {category.description}
                              </div>
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <Textarea
                              value={enhancements[category.key]}
                              onChange={(e) =>
                                onUpdateEnhancement(
                                  activeSettingsInstance,
                                  category.key,
                                  e.target.value,
                                )
                              }
                              placeholder={category.placeholder}
                              className="text-xs min-h-[60px] resize-none"
                              rows={2}
                            />

                            <div className="flex flex-col gap-1 flex-shrink-0">
                              {enhancements[category.key] && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() =>
                                    onClearEnhancement(activeSettingsInstance, category.key)
                                  }
                                  className="h-7 w-7"
                                  title="Clear and save to history"
                                  tabIndex={-1}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}

                              {enhancementHistory[category.key]?.length > 0 && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 relative group/history"
                                      tabIndex={-1}
                                    >
                                      <History className="h-4 w-4" />
                                      <div className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-popover border border-border rounded-md shadow-lg text-xs text-popover-foreground whitespace-nowrap opacity-0 invisible group-hover/history:opacity-100 group-hover/history:visible transition-all z-[100]">
                                        Description history
                                      </div>
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent
                                    align="end"
                                    className="w-64 max-h-48 overflow-y-auto"
                                  >
                                    <DropdownMenuLabel>Recent entries</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    {enhancementHistory[category.key].map((item, histIdx) => (
                                      <DropdownMenuItem
                                        key={histIdx}
                                        onClick={() =>
                                          onSelectFromHistory(
                                            activeSettingsInstance,
                                            category.key,
                                            item,
                                          )
                                        }
                                        className="text-xs truncate"
                                      >
                                        {item}
                                      </DropdownMenuItem>
                                    ))}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </div>

            {/* Sticky footer with preset buttons */}
            <div className="flex-shrink-0 px-6 py-4 border-t border-border bg-background">
              <div className="max-w-2xl mx-auto flex flex-col gap-2">
                {isUnsavedPresetName(config.presetName) &&
                  config.originalPresetId &&
                  presets.some((p) => p.id === config.originalPresetId) && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => onOverwritePreset(activeSettingsInstance)}
                      className="w-full gap-2 h-auto py-2 whitespace-normal text-left"
                    >
                      <RefreshCw className="h-4 w-4 flex-shrink-0" />
                      <span className="min-w-0">
                        Update &quot;
                        {presets.find((p) => p.id === config.originalPresetId)?.name}
                        &quot;
                      </span>
                    </Button>
                  )}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onOpenSavePresetDialog}
                    className="flex-1 gap-2"
                  >
                    <Save className="h-4 w-4" />
                    Save New Preset
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onOpenPresetsSheet}
                    className="flex-1 gap-2"
                  >
                    <BookMarked className="h-4 w-4" />
                    View Presets
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Save Preset Dialog */}
        {isSavePresetDialogOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200]">
            <div className="bg-background border border-border rounded-lg p-6 w-80 shadow-xl">
              <h3 className="text-lg font-semibold mb-4">Save New Preset</h3>
              <Input
                value={newPresetName}
                onChange={(e) => onNewPresetNameChange(e.target.value)}
                placeholder="Enter preset name..."
                className="mb-4"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newPresetName.trim()) {
                    onSavePreset();
                  } else if (e.key === 'Escape') {
                    onCloseSavePresetDialog();
                  }
                }}
              />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={onCloseSavePresetDialog}>
                  Cancel
                </Button>
                <Button size="sm" onClick={onSavePreset} disabled={!newPresetName.trim()}>
                  Save
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
